import React, { useRef, useContext, useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { HiOutlineArrowLeft, HiPrinter } from 'react-icons/hi';
import { MdEdit, MdDelete, MdOutlinePictureAsPdf } from 'react-icons/md';
import { BsBuilding } from 'react-icons/bs';

import { corporateService } from '../../services/corporation.service';
import GeneralModal from '../../helpers/GeneralModal';
import { createModal } from 'react-modal-promise';
import { SetCorporateEdit } from '../../store/corporateAtom';
import LogicContext from '../../Context/LogicContext';
import CorporateScroll from './CorporateScroll';
import { formatCustomDate } from '../../helpers/formatCustomDate';
import { formatCustomNumber } from '../../helpers/formatCustomNumber';
import StatusBadge from '../../generals/StatusBadge';

export default function CorporateDetailsPage() {
  const navigate = useNavigate();
  const { corporate_id } = useParams();
  const { pathname } = useLocation();
  const logicContext = useContext(LogicContext);
  const setEdit = SetCorporateEdit();

  const [corporateData, setCorporateData] = useState({});
  const [selectedList, setSelectedList] = useState([]);

  const componentRef = useRef();
  const myPromiseModal = createModal(GeneralModal);

  const currModuleId = pathname.split('/')[1];
  const currModuleName = pathname.split('/')[2];
  const currUserMenu = pathname.split('/')[3];

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Corporate_${corporateData?.Corporate_Short_Code || 'Details'}`,
  });

  useEffect(() => {
    document.title = 'Corporate Account | Details';

    let isMounted = true;
    corporateService
      .getCorporateDetails(corporate_id)
      .then(res => {
        if (isMounted && res.data?.Corporate_Info?.[0]) {
          setCorporateData(res.data.Corporate_Info[0]);
        }
      })
      .catch(() => {
        logicContext.showToast('Failed to load corporate details', 'error');
      });

    return () => {
      isMounted = false;
    };
  }, [corporate_id]);

  const goBack = () => navigate(-1);

  const confirmEdit = async () => {
    const confirmed = await myPromiseModal({
      title: 'Edit Corporate Record',
      body: 'Would you like to modify this corporate account?',
      showInput: false,
    });

    if (confirmed?.decision) {
      setEdit(corporateData);
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
    }
  };

  const onSelection = list => {
    setSelectedList(list.filter(item => item.checked));
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 pb-3 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <button onClick={goBack} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900">
                <HiOutlineArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Corporate Account</h1>
              {corporateData?.Corporate_Short_Code && (
                <span className="font-mono text-sm text-gray-500">#{corporateData.Corporate_Short_Code}</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button title="Print Details" onClick={handlePrint} className="rounded-lg p-2.5 text-gray-700 hover:bg-gray-100">
                <HiPrinter size={20} />
              </button>

              <button title="Export to PDF" className="rounded-lg p-2.5 text-gray-700 hover:bg-gray-100">
                <MdOutlinePictureAsPdf size={20} />
              </button>

              <button title="Edit" onClick={confirmEdit} className="rounded-lg p-2.5 text-blue-600 hover:bg-gray-100 hover:text-blue-700">
                <MdEdit size={20} />
              </button>

              <button title="Delete" className="rounded-lg p-2.5 text-rose-600 hover:bg-gray-100 hover:text-rose-700">
                <MdDelete size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl pt-6">
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Sidebar - Corporate List */}
          <div className="flex-shrink-0 lg:w-80 xl:w-96">
            <div className="sticky top-20 overflow-hidden rounded-xl bg-white">
              <CorporateScroll
                onSelection={onSelection}
                viewItemDetails={id => navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${id}`)}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div ref={componentRef} className="rounded-xl border border-gray-200 bg-white p-8 print:border-0 print:shadow-none">
              {/* Header */}
              <div className="flex flex-col gap-6 border-b pb-8 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-indigo-50">
                    <BsBuilding className="h-9 w-9 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{corporateData?.Corporate_Name || 'Corporate Account'}</h2>
                    <p className="mt-1 font-mono text-sm text-gray-500">{corporateData?.Corporate_Short_Code || '—'}</p>
                  </div>
                </div>

                <div className="text-right">
                  <StatusBadge
                    statusId={corporateData?.Corporate_Approval_Status_Id}
                    statusName={corporateData?.Corporate_Approval_Status_Name}
                    size="md"
                  />
                </div>
              </div>

              {/* Details Grid */}
              <div className="mt-8 grid grid-cols-1 gap-x-12 gap-y-7 text-sm md:grid-cols-2 lg:grid-cols-3">
                {/* Column 1 */}
                <div className="space-y-6">
                  <div>
                    <dt className="font-medium text-gray-600">Activation Date</dt>
                    <dd className="mt-1 font-medium text-gray-900">
                      {formatCustomDate(corporateData?.subscription_activation_date) || '—'}
                    </dd>
                  </div>

                  <div>
                    <dt className="font-medium text-gray-600">Registration Date</dt>
                    <dd className="mt-1 font-medium text-gray-900">{formatCustomDate(corporateData?.Registration_Date) || '—'}</dd>
                  </div>

                  <div>
                    <dt className="font-medium text-gray-600">No. of Employees</dt>
                    <dd className="mt-1 font-medium text-gray-900">{corporateData?.Number_Of_Officer_Name || '—'}</dd>
                  </div>

                  <div>
                    <dt className="font-medium text-gray-600">Corporate Type</dt>
                    <dd className="mt-1 font-medium text-gray-900">{corporateData?.Corporate_Type_Name || '—'}</dd>
                  </div>
                </div>

                {/* Column 2 */}
                <div className="space-y-6">
                  <div>
                    <dt className="font-medium text-gray-600">Email</dt>
                    <dd className="mt-1 break-words font-medium text-gray-900">{corporateData?.Corporate_Email_Address || '—'}</dd>
                  </div>

                  <div>
                    <dt className="font-medium text-gray-600">Contact Person</dt>
                    <dd className="mt-1 font-medium text-gray-900">{corporateData?.Corporate_Contact_Person || '—'}</dd>
                  </div>

                  <div>
                    <dt className="font-medium text-gray-600">Contact Number</dt>
                    <dd className="mt-1 font-medium text-gray-900">{corporateData?.Corporate_Contact_Person_Mobile || '—'}</dd>
                  </div>

                  <div>
                    <dt className="font-medium text-gray-600">Address</dt>
                    <dd className="mt-1 text-gray-800">{corporateData?.Corporate_Adddress || '—'}</dd>
                  </div>
                </div>

                {/* Column 3 */}
                <div className="space-y-6">
                  <div>
                    <dt className="font-medium text-gray-600">Subscription Plan</dt>
                    <dd className="mt-1 font-medium text-gray-900">{corporateData?.Subscription_Name || '—'}</dd>
                  </div>

                  <div>
                    <dt className="font-medium text-gray-600">Trial Period</dt>
                    <dd className="mt-1 font-medium text-gray-900">
                      {corporateData?.trail_period ? `${corporateData.trail_period} ${corporateData.trail_period_type_name || ''}` : '—'}
                    </dd>
                  </div>

                  <div>
                    <dt className="font-medium text-gray-600">Subscription Period</dt>
                    <dd className="mt-1 font-medium text-gray-900">{corporateData?.Subscription_Period || '—'}</dd>
                  </div>

                  <div>
                    <dt className="font-medium text-gray-600">Default Bank</dt>
                    <dd className="mt-1 font-medium text-gray-900">{corporateData?.Default_Bank_Transaction_Name || '—'}</dd>
                  </div>

                  <div>
                    <dt className="font-medium text-gray-600">Max Discount</dt>
                    <dd className="mt-1 font-medium text-emerald-600">
                      {corporateData?.maximum_discount_percentage ? `${corporateData.maximum_discount_percentage}%` : '—'}
                    </dd>
                  </div>

                  <div>
                    <dt className="font-medium text-gray-600">Subscription Fee</dt>
                    <dd className="mt-1 font-bold text-gray-900">₦{formatCustomNumber(corporateData?.subscription_fees) || '0.00'}</dd>
                  </div>
                </div>
              </div>

              {/* Approval Section */}
              <div className="mt-12 border-t pt-8">
                <h3 className="mb-5 text-lg font-semibold text-gray-800">Approval Information</h3>
                <div className="grid grid-cols-1 gap-8 text-sm md:grid-cols-2">
                  <div className="space-y-4">
                    <div>
                      <dt className="text-gray-600">First Approval</dt>
                      <dd className="mt-1 font-medium">{corporateData?.First_Level_Encoded_By_Name || '—'}</dd>
                      <dd className="mt-0.5 text-gray-500">{formatCustomDate(corporateData?.First_Level_Encoded_Date) || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Remark</dt>
                      <dd className="mt-1 italic text-gray-700">{corporateData?.First_Level_Remark || 'No remark'}</dd>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <dt className="text-gray-600">Second Approval</dt>
                      <dd className="mt-1 font-medium">{corporateData?.Second_Level_Encoded_By_Name || '—'}</dd>
                      <dd className="mt-0.5 text-gray-500">{formatCustomDate(corporateData?.Second_Level_Encoded_Date) || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Remark</dt>
                      <dd className="mt-1 italic text-gray-700">{corporateData?.Second_Level_Remark || 'No remark'}</dd>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
